import React from 'react';
import {
  Box, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import { humanMs } from '../operations/areas';

// Where this load's time went, and where it lost it.
//
// The question people arrive with is not "how long did this take" - the trip header already says
// that - but "which part of it was the problem". A list of durations leaves that to the reader, who
// has to know what each leg usually takes before any of the numbers mean anything. So each leg is
// shown against its own normal, and the one that cost the most is named outright.
//
// Nothing here is worked out on the page. The server learns each route's normals once an hour and
// writes the comparison onto the trip, because learning them means replaying forty finished
// journeys and a board with twenty running loads would otherwise do that twenty times over.

/**
 * What makes a leg itself: the place, and which call at it this is.
 *
 * The same identity the server keys a leg's normal by. It matters on a shuttle, whose route returns
 * to where it started - loading at the origin and pulling in at the end of the run are the same
 * point and not the same job.
 */
export const legKey = (leg) => (leg.kind === 'STOP'
  ? `stop:${leg.checkpointId}:${leg.nth}`
  : `road:${leg.fromCheckpointId}:${leg.toCheckpointId}:${leg.nth}`);

/** A difference, in the words somebody would use for it. */
export const lateness = (overMs) => {
  if (overMs == null) return null;
  const mins = Math.round(overMs / 60000);
  if (mins === 0) return { text: 'on time', tone: 'success' };
  return mins > 0
    ? { text: `${humanMs(overMs)} over`, tone: 'error' }
    : { text: `${humanMs(-overMs)} under`, tone: 'success' };
};

/** The headline: what it took, what it usually takes, and where the difference went. */
export const summary = (delay) => {
  if (!delay) return null;
  const late = delay.lateMs > 0;
  return {
    took: humanMs(delay.totalMs),
    // Only the legs that could be judged are in the usual figure, so the two numbers compare like
    // with like rather than setting a whole journey against part of one.
    usual: delay.normalMs > 0 ? humanMs(delay.normalMs) : null,
    late,
    lateText: late ? humanMs(delay.lateMs) : null,
    worst: late && delay.worstName ? delay.worstName : null,
    worstText: late && delay.worstMs ? humanMs(delay.worstMs) : null,
  };
};

const DelayPanel = ({ trip }) => {
  const delay = trip?.delay;
  const legs = delay?.legs || [];
  // Nothing measured yet: the caller does not draw the section at all, so there is nothing to say
  // here. A panel that explained the conditions under which it would have had something to show was
  // a paragraph of apology where a person wanted a number.
  if (!legs.length) return null;
  const head = summary(delay);
  // Bars are drawn against the longest leg, so the biggest part of the journey fills the row and the
  // rest are read against it. Proportional to the whole would make every short stop invisible.
  const longest = legs.reduce((held, leg) => Math.max(held, leg.ms || 0), 0) || 1;

  return (
    <Stack gap={2}>
      <Box>
        <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap">
          <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>{head.took}</Typography>
          {head.usual && <Typography color="text.secondary">{`against a usual ${head.usual}`}</Typography>}
          {head.late && <Chip size="small" color="error" label={`${head.lateText} late`} />}
          {!head.late && head.usual && <Chip size="small" color="success" variant="outlined" label="On time" />}
        </Stack>
        {head.worst && (
          <Typography variant="body2" color="error.main" fontWeight={600} sx={{ mt: 0.5 }}>
            {`Most of it at ${head.worst} — ${head.worstText} over.`}
          </Typography>
        )}
      </Box>

      <Stack gap={1.25}>
        {legs.map((leg) => {
          const late = lateness(leg.overMs);
          const unknown = leg.normalFrom === 'NOT_ENOUGH_HISTORY';
          return (
            <Box key={legKey(leg)}>
              <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap">
                <Typography variant="body2" fontWeight={leg.kind === 'STOP' ? 700 : 400} sx={{ flex: 1, minWidth: 0 }}>
                  {leg.kind === 'ROAD' ? `↓ ${leg.name}` : leg.name}
                </Typography>
                <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {humanMs(leg.ms)}
                  {leg.running && <Typography component="span" variant="caption" color="text.secondary">{' so far'}</Typography>}
                </Typography>
                {unknown
                  ? (
                    <Tooltip title={`Seen ${leg.runs || 0} time${leg.runs === 1 ? '' : 's'} before. Too few to say what is normal here.`}>
                      <Chip size="small" variant="outlined" label="No history yet" />
                    </Tooltip>
                  )
                  : late && (
                    <Tooltip title={`Usually ${humanMs(leg.normalMs)}${leg.normalFrom === 'PLANNED' ? ', as planned' : `, from ${leg.runs} previous runs`}`}>
                      <Chip
                        size="small"
                        color={late.tone}
                        variant={late.tone === 'error' ? 'filled' : 'outlined'}
                        label={late.text}
                      />
                    </Tooltip>
                  )}
              </Stack>
              {/* The time this leg took, with where it should have ended marked on it. A bar that
                  simply got longer would say a leg was big; the mark is what says it was late. */}
              <Box sx={{ position: 'relative', height: 8, borderRadius: 1, bgcolor: 'action.hover', mt: 0.5 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    right: 'auto',
                    height: '100%',
                    borderRadius: 1,
                    width: `${Math.max(1, ((leg.ms || 0) / longest) * 100)}%`,
                    bgcolor: leg.overMs > 0 ? 'error.main' : leg.kind === 'STOP' ? 'primary.main' : 'text.disabled',
                    opacity: leg.running ? 0.6 : 1,
                  }}
                />
                {leg.normalMs > 0 && (
                  <Tooltip title={`Usually ${humanMs(leg.normalMs)}`}>
                    <Box sx={{
                      position: 'absolute',
                      top: -2,
                      bottom: -2,
                      width: 2,
                      bgcolor: 'text.primary',
                      left: `${Math.min(100, (leg.normalMs / longest) * 100)}%`,
                    }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default DelayPanel;
